import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        lineHeight: 1.55,
        fontFamily: "Helvetica",
    },
    header: {
        marginBottom: 20,
        paddingBottom: 12,
        borderBottomWidth: 2,
        borderBottomColor: "#16A34A",
    },
    school: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 4,
    },
    title: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 10,
        color: "#777777",
    },
    section: {
        marginBottom: 14,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: "#16A34A",
    },
    text: {
        fontSize: 10,
        color: "#333333",
        marginBottom: 4,
    },
    bullet: {
        marginLeft: 10,
        marginBottom: 3,
        fontSize: 10,
    },
    metaGrid: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 18,
    },
    metaItem: {
        width: "50%",
        marginBottom: 7,
    },
    metaLabel: {
        fontSize: 8,
        color: "#777777",
        textTransform: "uppercase",
    },
    metaValue: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#222222",
    },
    warning: {
        marginBottom: 15,
        padding: 10,
        backgroundColor: "#F5F5F5",
        borderLeftWidth: 3,
        borderLeftColor: "#C8A74E",
    },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 40,
        right: 40,
        textAlign: "center",
        fontSize: 8,
        color: "#888888",
    },
});

const normalize = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value.trim();
    }

    if (Array.isArray(value)) {
        return value
            .map(normalize)
            .filter(Boolean)
            .join("\n");
    }

    if (typeof value === "object") {
        if (value.text) {
            return normalize(value.text);
        }

        if (value.content) {
            return normalize(value.content);
        }

        return Object.entries(value)
            .map(
                ([key, val]) =>
                    `${key}: ${normalize(val)}`
            )
            .join("\n");
    }

    return String(value);
};

const field = (data, ...keys) => {
    for (const key of keys) {
        if (
            data &&
            data[key] !== undefined &&
            data[key] !== null &&
            normalize(data[key])
        ) {
            return data[key];
        }
    }

    return "";
};

const renderText = (value) => {
    const text = normalize(value);

    if (!text) {
        return (
            <Text style={styles.text}>
                Not specified.
            </Text>
        );
    }

    return text.split("\n").map((line, index) => {
        const clean = line.trim();

        if (!clean) {
            return (
                <Text key={index}>
                    {" "}
                </Text>
            );
        }

        if (
            clean.startsWith("-") ||
            clean.startsWith("•") ||
            /^\d+[.)]/.test(clean)
        ) {
            return (
                <Text
                    key={index}
                    style={styles.bullet}
                >
                    {clean}
                </Text>
            );
        }

        return (
            <Text
                key={index}
                style={styles.text}
            >
                {clean}
            </Text>
        );
    });
};

const AiTeacherNotesPDF = ({
    lessonNote = {},
    topic = {},
    academicYear = "",
}) => {
    const notes =
        lessonNote?.content ||
        lessonNote?.lesson_note ||
        lessonNote?.teacher_notes ||
        lessonNote?.data ||
        lessonNote;

    const topicName =
        topic?.name ||
        lessonNote?.topic_name ||
        lessonNote?.topic?.name ||
        "Curriculum Topic";

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.school}>
                        ROSE INTERNATIONAL SCHOOL
                    </Text>

                    <Text style={styles.title}>
                        AI Teacher Notes
                    </Text>

                    <Text style={styles.subtitle}>
                        Curriculum Teaching Assistant
                    </Text>
                </View>

                <View style={styles.metaGrid}>
                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Topic
                        </Text>

                        <Text style={styles.metaValue}>
                            {topicName}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Subject
                        </Text>

                        <Text style={styles.metaValue}>
                            {topic?.subject_name ||
                                topic?.subject?.name ||
                                "Not specified"}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Grade
                        </Text>

                        <Text style={styles.metaValue}>
                            {topic?.grade_name ||
                                topic?.grade_class_name ||
                                topic?.grade_class?.name ||
                                "Not specified"}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Academic Year
                        </Text>

                        <Text style={styles.metaValue}>
                            {academicYear ||
                                lessonNote?.academic_year_name ||
                                "Not specified"}
                        </Text>
                    </View>
                </View>

                <View style={styles.warning}>
                    <Text style={styles.text}>
                        These AI-generated teacher notes are grounded
                        in the selected official curriculum topic.
                        Review and adapt them to your classroom before
                        teaching.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Lesson Introduction
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "introduction",
                            "lesson_introduction",
                            "opening"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Key Concepts
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "key_concepts",
                            "concepts",
                            "main_concepts"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Teacher Explanation
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "teacher_explanation",
                            "explanation",
                            "content",
                            "notes"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Examples
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "examples",
                            "worked_examples",
                            "illustrations"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Classroom Activities
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "activities",
                            "classroom_activities",
                            "student_activities"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Questions to Ask Students
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "questions",
                            "teacher_questions",
                            "discussion_questions"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Common Misconceptions
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "misconceptions",
                            "common_misconceptions"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Assessment
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "assessment",
                            "formative_assessment",
                            "assessment_guidance"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Homework / Extension
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "homework",
                            "extension",
                            "practice"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Teacher Reminder
                    </Text>

                    {renderText(
                        field(
                            notes,
                            "teacher_reminder",
                            "reminder",
                            "final_notes"
                        )
                    )}
                </View>

                <Text style={styles.footer}>
                    AI-generated teacher teaching aid •
                    Based on official curriculum content
                </Text>
            </Page>
        </Document>
    );
};

export default AiTeacherNotesPDF;